import { PlusOutlined } from "@ant-design/icons";
import { Button, Divider } from "antd";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppContext from "../../context/app/context";
import { useIsMount } from "../../utils/hooks/is-mount";
import styles from "./style.module.scss";
import Can from "../../utils/rbac/can";
import PageHeader from "../../components/general/layout/page-header";
import RefreshBtn from "../../components/general/table-components/actions/refresh-btn";
import { Permissions } from "../../utils/Permissions/Permissions";
import ReportContextProvider from "../../context/report/provider";
import ReportContext from "../../context/report/context";
import ReportList from "../../components/report/report-list";
import SearchFilterCard from "../../components/general/filter-card";
import FormHookModal from "../../components/general/modals/form-hook-modal";
import { useModal } from "react-modal-hook";
import ReportForm from "../../components/report/report-form";
import ReportFilter from "../../components/report/report-filter";
import LowStockReportTable from "../../components/report/low-stock-report-table";
import { ReportType } from "../../models/report/enum";
import { ICreateReport } from "../../models/report/request";

interface IProps {
  children?: React.ReactNode;
}

const buildLowStockReportRequest = (data: any): ICreateReport => {
  const request: ICreateReport = {
    store_id: data.store_id,
    threshold: data.threshold,
  };

  if (data.startDate) {
    request.startDate = data.startDate;
  }

  if (data.startDate && data.endDate) {
    request.endDate = data.endDate;
  }

  return request;
};

const buildSavedReportRequest = (data: any): ICreateReport => {
  switch (data.type) {
    case ReportType.DRIVER_PAYMENTS: {
      const request: ICreateReport = {};

      if (data.startDate) request.startDate = data.startDate;
      if (data.endDate) request.endDate = data.endDate;
      if (data.driverId) request.driverId = data.driverId;

      return request;
    }

    case ReportType.USER_ACTIVITY_REPORT: {
      const request: ICreateReport = {};

      if (data.startDate) request.startDate = data.startDate;
      if (data.endDate) request.endDate = data.endDate;
      if (data.min_orders !== undefined && data.min_orders !== "")
        request.min_orders = data.min_orders;
      if (data.max_orders !== undefined && data.max_orders !== "")
        request.max_orders = data.max_orders;

      return request;
    }

    case ReportType.MONTHLY_USER_ACTIVITY_REPORT: {
      const request: ICreateReport = {};

      if (data.startDate) request.startDate = data.startDate;
      if (data.endDate) request.endDate = data.endDate;
      if (
        data.min_orders_per_month !== undefined &&
        data.min_orders_per_month !== ""
      ) {
        request.min_orders_per_month = data.min_orders_per_month;
      }

      return request;
    }

    case ReportType.PRODUCT_MARGIN_REPORT: {
      const request: ICreateReport = {};

      if (data.store_id) request.store_id = data.store_id;
      if (data.trademark_id) request.trademark_id = data.trademark_id;
      if (data.product_status) request.product_status = data.product_status;
      if (data.store_quantity_status) {
        request.store_quantity_status = data.store_quantity_status;
      }

      return request;
    }

    default: {
      const request: ICreateReport = {};

      if (data.startDate) request.startDate = data.startDate;
      if (data.endDate) request.endDate = data.endDate;

      return request;
    }
  }
};

const Report: React.FC<IProps> = () => {
  const { t } = useTranslation();

  const { direction } = useContext(AppContext);

  const [search, setSearch] = useState<string | undefined>(undefined);

  const { actions, loading, query } = useContext(ReportContext);

  useEffect(() => {
    actions.setSearch(search);
  }, [search]);

  const isMounted = useIsMount();
  useEffect(() => {
    !isMounted && actions.getData();
  }, [query, direction]);

  const [showFormModal, hideForModal] = useModal(
    () => (
      <FormHookModal
        id="4"
        confirmButtonText={t("save")}
        loading={loading.includes("create")}
        onSubmit={async (data) => {
          if (data.type === ReportType.LOW_STOCK_REPORT) {
            await actions.createLowStockReport(buildLowStockReportRequest(data));
          } else {
            await actions.createReport(buildSavedReportRequest(data), data.type);
          }
        }}
        hideModal={() => {
          hideForModal();
        }}
        width={"50vw"}
        style={{
          marginTop: "1.6rem",
          height: "59vh",
        }}
        title={t("add_report")}
      >
        <ReportForm />
      </FormHookModal>
    ),
    [loading]
  );

  return (
    <div dir={direction} className={styles.container}>
      <PageHeader
        title={t("reports")}
        subTitle={t("reports_management")}
        extra={[
          <RefreshBtn
            key={0}
            loading={loading.includes("list")}
            onClick={() => actions.getData()}
          >
            {t("refresh")}
          </RefreshBtn>,
          <Divider key={1} type="vertical" />,
          <Can key={2} perform={Permissions.CAN_VIEW_REPORTS}>
            <Button
              type="primary"
              onClick={() => {
                showFormModal();
              }}
              icon={<PlusOutlined />}
            >
              {t("add_report")}
            </Button>
          </Can>,
        ]}
      />
      <SearchFilterCard
        formId="report-filter"
        searchValue={search}
        setSearchValue={setSearch}
        onReset={() => {
          actions.setQuery({
            page: query?.page,
            perPage: query?.perPage,
          });
        }}
        resetLoading={loading.includes("list")}
        applyLoading={loading.includes("list")}
      >
        <ReportFilter></ReportFilter>
      </SearchFilterCard>

      <ReportList></ReportList>

      <LowStockReportTable />
    </div>
  );
};

const ReportPage = () => {
  return (
    // ContextProvider
    <ReportContextProvider>
      <Report />
    </ReportContextProvider>
  );
};
export default ReportPage;
